/**
 * COLOUR PRIMITIVES — every colour value either portal paints with.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Components never write a hex or rgba() literal; they read one of these (or a
 * semantic alias in `tokens.js` built on top of them). Change a value here and
 * every screen that uses it follows.
 *
 * HOW THE NAMES ARE CHOSEN
 * The values were lifted out of the screens unchanged, so the names say where
 * each one came from rather than pretending there was one system all along:
 *
 *   white / black      — exactly #FFFFFF / #000000.
 *   zenvoya.*          — the house brand: the CRM MUI theme, the partner
 *                        branding fallback, and the legacy partner demo purple
 *                        (`legacyPartnerPrimary`, still the `--pa-brand-*`
 *                        fallback in `partnerBrand`).
 *   neutral[step]      — pure greys (R = G = B, plus a few near-greys). The step
 *                        is darkness in tenths of a percent: 0 is white, 1000 is
 *                        black, so a bigger number is always a darker grey.
 *   tw.<hue>[step]     — an exact Tailwind default-palette colour.
 *   mui.<hue>[step]    — an exact `@mui/material/colors` colour.
 *   antd.<hue>[step]   — an exact AntD preset-palette colour (1–10).
 *   custom.<hue>[step] — anything else; hue by HSL angle, step by darkness as
 *                        for `neutral`.
 *   alpha.<base>[pct]  — translucent colours: `alpha.black[8]` is black at 8%.
 *
 * Several near-duplicates exist (e.g. three slightly different light greys).
 * That is faithful to what the screens rendered; merging them is a design
 * decision, made by repointing call sites, not by editing values here.
 *
 * Values are plain hex / rgba strings on purpose: they are also fed to AntD's
 * colour algorithm and to contrast maths, which cannot take a `var()`.
 */
export const palette = {
  white: '#FFFFFF',
  black: '#000000',
  zenvoya: {
    legacyPartnerPrimary: '#7C5CFC',
    lime: '#A1F919',
    primary: '#6C00F6',
    primaryDark: '#5009A8',
    primaryLight: '#8B3FE8',
    secondary: '#98D629',
    secondaryDark: '#7AB31E',
    secondaryLight: '#B0E34D',
    sky: '#00B8FF',
    success: '#01A53B',
  },
  neutral: {
    20: '#FAFAFA',
    21: '#F9F9F9',
    30: '#F8F8F8',
    31: '#F7F7F7',
    40: '#F5F5F5',
    41: '#F5F5F7',
    50: '#F2F2F2',
    51: '#F1F1F1',
    52: '#F0F1F3',
    60: '#F0F0F0',
    70: '#EEEEEE',
    90: '#E8E8E8',
    100: '#E6E6E6',
    101: '#E5E5E5',
    110: '#E5E4E2',
    120: '#E0E0E0',
    130: '#DDDDDD',
    150: '#D9D9D9',
    170: '#D1D1D1',
    180: '#D0D0D0',
    200: '#CCCCCC',
    250: '#C0C0C0',
    251: '#BFBFBF',
    260: '#BDBDBD',
    270: '#BBBBBB',
    330: '#AAAAAA',
    380: '#9E9E9E',
    400: '#999999',
    450: '#8C8C8C',
    470: '#888888',
    530: '#777777',
    580: '#6B6B6B',
    590: '#696969',
    591: '#5F6A70',
    600: '#666666',
    650: '#595959',
    670: '#555555',
    730: '#444444',
    800: '#333333',
    820: '#2D2D2D',
    850: '#272727',
    870: '#222222',
    880: '#1E1E1E',
    900: '#1A1A1A',
    930: '#111111',
  },
  tw: {
    amber: {
      50: '#FFFBEB',
      100: '#FEF3C7',
      200: '#FDE68A',
      500: '#F59E0B',
      600: '#D97706',
      700: '#B45309',
      800: '#92400E',
    },
    blue: {
      50: '#EFF6FF',
      100: '#DBEAFE',
      200: '#BFDBFE',
      500: '#3B82F6',
      600: '#2563EB',
      700: '#1D4ED8',
      800: '#1E40AF',
    },
    cyan: {
      100: '#CFFAFE',
      800: '#155E75',
    },
    emerald: {
      50: '#ECFDF5',
      100: '#D1FAE5',
      500: '#10B981',
      600: '#059669',
      800: '#065F46',
    },
    gray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      900: '#111827',
    },
    green: {
      50: '#F0FDF4',
      100: '#DCFCE7',
      200: '#BBF7D0',
      500: '#22C55E',
      600: '#16A34A',
      800: '#166534',
    },
    indigo: {
      50: '#EEF2FF',
      100: '#E0E7FF',
      200: '#C7D2FE',
      300: '#A5B4FC',
      400: '#818CF8',
      500: '#6366F1',
      600: '#4F46E5',
      800: '#3730A3',
    },
    orange: {
      50: '#FFF7ED',
      600: '#EA580C',
      700: '#C2410C',
    },
    pink: {
      50: '#FDF2F8',
      100: '#FCE7F3',
      500: '#EC4899',
      700: '#BE185D',
      800: '#9D174D',
    },
    purple: {
      50: '#FAF5FF',
      100: '#F3E8FF',
      200: '#E9D5FF',
    },
    red: {
      50: '#FEF2F2',
      100: '#FEE2E2',
      200: '#FECACA',
      400: '#F87171',
      500: '#EF4444',
      600: '#DC2626',
      800: '#991B1B',
    },
    rose: {
      50: '#FFF1F2',
      100: '#FFE4E6',
      700: '#BE123C',
      800: '#9F1239',
    },
    violet: {
      50: '#F5F3FF',
      100: '#EDE9FE',
      200: '#DDD6FE',
      300: '#C4B5FD',
      400: '#A78BFA',
      500: '#8B5CF6',
      600: '#7C3AED',
      700: '#6D28D9',
      800: '#5B21B6',
    },
    yellow: {
      50: '#FEFCE8',
    },
  },
  mui: {
    amber: {
      50: '#FFF8E1',
      200: '#FFE082',
    },
    blue: {
      50: '#E3F2FD',
      100: '#BBDEFB',
      700: '#1976D2',
      800: '#1565C0',
    },
    blueGrey: {
      50: '#ECEFF1',
      600: '#546E7A',
      800: '#37474F',
    },
    cyan: {
      50: '#E0F7FA',
      800: '#00838F',
    },
    deepPurple: {
      800: '#4527A0',
    },
    green: {
      50: '#E8F5E9',
      600: '#43A047',
      800: '#2E7D32',
    },
    indigo: {
      50: '#E8EAF6',
      600: '#3949AB',
      800: '#283593',
    },
    lightBlue: {
      800: '#0277BD',
    },
    orange: {
      50: '#FFF3E0',
      100: '#FFE0B2',
      900: '#E65100',
    },
    pink: {
      50: '#FCE4EC',
      800: '#AD1457',
    },
    purple: {
      50: '#F3E5F5',
      700: '#7B1FA2',
    },
    red: {
      50: '#FFEBEE',
      700: '#D32F2F',
      800: '#C62828',
    },
    yellow: {
      800: '#F9A825',
      900: '#F57F17',
    },
  },
  antd: {
    blue: {
      1: '#E6F4FF',
      3: '#91CAFF',
      6: '#1677FF',
      7: '#0958D9',
    },
    geekblue: {
      1: '#F0F5FF',
      3: '#ADC6FF',
    },
    gold: {
      6: '#FAAD14',
    },
    green: {
      1: '#F6FFED',
      3: '#B7EB8F',
      6: '#52C41A',
      7: '#389E0D',
    },
    orange: {
      1: '#FFF7E6',
      3: '#FFD591',
      6: '#FA8C16',
      7: '#D46B08',
    },
    purple: {
      6: '#722ED1',
    },
    red: {
      1: '#FFF1F0',
      3: '#FFA39E',
      5: '#FF4D4F',
      7: '#CF1322',
    },
  },
  custom: {
    amber: {
      20: '#FFFDF5',
      50: '#FFF8E7',
      120: '#FFF0C2',
      230: '#E8D5A0',
      410: '#FEBC2E',
      460: '#C9A84C',
      470: '#F5C518',
      510: '#FCB400',
      530: '#C9A227',
      680: '#8A6D1B',
      790: '#6D4C00',
    },
    blue: {
      20: '#F5F7FF',
      21: '#F8F9FB',
      22: '#F4F7FE',
      30: '#F5F6FA',
      31: '#F5F6F8',
      50: '#E6F0FF',
      70: '#E1E9F8',
      100: '#E3E5E9',
      270: '#B0B7C3',
      271: '#A3AED0',
      440: '#707EAE',
      800: '#1B254B',
    },
    cyan: {
      290: '#6AD2FF',
      300: '#6AD2FA',
      670: '#0077A8',
    },
    green: {
      450: '#6FA88A',
      480: '#3FC93F',
      530: '#28C840',
      650: '#1F9254',
      730: '#008B32',
    },
    indigo: {
      10: '#FAFAFF',
      40: '#F0ECFF',
      60: '#EDEBF5',
      250: '#9B7DFF',
      390: '#5E37FF',
      450: '#4318FF',
      490: '#5A3DC7',
      750: '#11047A',
      830: '#1E1B3A',
      880: '#13112A',
      930: '#0E0C1A',
      960: '#08070F',
    },
    lime: {
      110: '#EEFFC9',
      240: '#D7FF84',
      640: '#6EA90F',
    },
    orange: {
      500: '#CD7F32',
      530: '#ED6C02',
    },
    red: {
      30: '#FFF0F0',
      330: '#FF5F57',
      410: '#F53939',
      460: '#DC3545',
      630: '#A01F1F',
    },
    sky: {
      20: '#FAFBFC',
      21: '#F8F9FA',
      30: '#F6F8FA',
      40: '#EFF4FB',
      50: '#F1F3F4',
      51: '#EEF2F6',
      70: '#E8ECF0',
      90: '#E3E8EF',
      160: '#CBD5E0',
      310: '#A0AEC0',
      510: '#6B7B8D',
    },
    teal: {
      760: '#1B5E3F',
    },
    violet: {
      20: '#F9F5FE',
      50: '#F3EBFC',
      51: '#F0E6FF',
      70: '#EBDDFF',
      80: '#E8D5FF',
      200: '#C9B6E4',
      220: '#C4A8E8',
      260: '#B07CFF',
      290: '#B07AEE',
      330: '#A990C8',
      360: '#9B7CC8',
      500: '#7B5FA0',
      550: '#6910D4',
      580: '#6B5087',
      670: '#4A00A8',
      800: '#2D0066',
      850: '#1E0E3E',
      890: '#140A2E',
    },
    yellow: {
      500: '#FFD700',
    },
  },
  alpha: {
    antdRed5: {
      4: 'rgba(255, 77, 79, 0.04)',
    },
    black: {
      3: 'rgba(0, 0, 0, 0.03)',
      4: 'rgba(0, 0, 0, 0.04)',
      5: 'rgba(0, 0, 0, 0.05)',
      6: 'rgba(0, 0, 0, 0.06)',
      8: 'rgba(0, 0, 0, 0.08)',
      10: 'rgba(0, 0, 0, 0.1)',
      12: 'rgba(0, 0, 0, 0.12)',
      15: 'rgba(0, 0, 0, 0.15)',
      20: 'rgba(0, 0, 0, 0.2)',
      25: 'rgba(0, 0, 0, 0.25)',
      30: 'rgba(0, 0, 0, 0.3)',
      45: 'rgba(0, 0, 0, 0.45)',
      50: 'rgba(0, 0, 0, 0.5)',
      54: 'rgba(0, 0, 0, 0.54)',
      55: 'rgba(0, 0, 0, 0.55)',
      60: 'rgba(0, 0, 0, 0.6)',
    },
    customBlue271: {
      30: 'rgba(163, 174, 208, 0.3)',
    },
    customIndigo450: {
      100: 'rgba(67, 24, 255, 1)',
    },
    customLime640: {
      50: 'rgba(110, 169, 15, 0.5)',
    },
    customViolet260: {
      10: 'rgba(176, 124, 255, 0.1)',
      12: 'rgba(176, 124, 255, 0.12)',
      15: 'rgba(176, 124, 255, 0.15)',
      25: 'rgba(176, 124, 255, 0.25)',
    },
    customViolet800: {
      8: 'rgba(45, 0, 102, 0.08)',
    },
    twGray400: {
      30: 'rgba(156, 163, 175, 0.3)',
      50: 'rgba(156, 163, 175, 0.5)',
    },
    twGray900: {
      12: 'rgba(17, 24, 39, 0.12)',
    },
    twRed500: {
      3: 'rgba(239, 68, 68, 0.03)',
    },
    white: {
      4: 'rgba(255, 255, 255, 0.04)',
      8: 'rgba(255, 255, 255, 0.08)',
      15: 'rgba(255, 255, 255, 0.15)',
      20: 'rgba(255, 255, 255, 0.2)',
      25: 'rgba(255, 255, 255, 0.25)',
      30: 'rgba(255, 255, 255, 0.3)',
      45: 'rgba(255, 255, 255, 0.45)',
      50: 'rgba(255, 255, 255, 0.5)',
      70: 'rgba(255, 255, 255, 0.7)',
      75: 'rgba(255, 255, 255, 0.75)',
      80: 'rgba(255, 255, 255, 0.8)',
      85: 'rgba(255, 255, 255, 0.85)',
      95: 'rgba(255, 255, 255, 0.95)',
    },
    zenvoyaLegacyPartnerPrimary: {
      4: 'rgba(124, 92, 252, 0.04)',
      8: 'rgba(124, 92, 252, 0.08)',
      20: 'rgba(124, 92, 252, 0.2)',
    },
    zenvoyaLime: {
      6: 'rgba(161, 249, 25, 0.06)',
    },
    zenvoyaPrimary: {
      3: 'rgba(108, 0, 246, 0.03)',
      6: 'rgba(108, 0, 246, 0.06)',
      8: 'rgba(108, 0, 246, 0.08)',
      12: 'rgba(108, 0, 246, 0.12)',
      15: 'rgba(108, 0, 246, 0.15)',
      25: 'rgba(108, 0, 246, 0.25)',
      30: 'rgba(108, 0, 246, 0.3)',
      40: 'rgba(108, 0, 246, 0.4)',
    },
    zenvoyaPrimaryLight: {
      6: 'rgba(139, 63, 232, 0.06)',
      10: 'rgba(139, 63, 232, 0.1)',
      15: 'rgba(139, 63, 232, 0.15)',
      18: 'rgba(139, 63, 232, 0.18)',
      20: 'rgba(139, 63, 232, 0.2)',
      35: 'rgba(139, 63, 232, 0.35)',
    },
    zenvoyaSky: {
      5: 'rgba(0, 184, 255, 0.05)',
    },
  },
};

export default palette;
